/**
 * Utility functions for handling images
 */

/**
 * Generate a random hex color
 * @returns Random hex color string (e.g. #FF5733)
 */
const getRandomColor = (): string => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

/**
 * Get placeholder image URL when image is empty
 * @param text Text to display on the placeholder image
 * @param width Width of the placeholder image (default: 300)
 * @param height Height of the placeholder image (default: 200)
 * @returns Placeholder image URL from placehold.co
 */
export const getPlaceholderImage = (
  text: string,
  width: number = 300,
  height: number = 200
): string => {
  // Generate random background color
  const bgColor = getRandomColor().replace('#', '');
  
  // Generate a contrasting text color (simple version - white or black based on background brightness)
  const r = parseInt(bgColor.substring(0, 2), 16);
  const g = parseInt(bgColor.substring(2, 4), 16);
  const b = parseInt(bgColor.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  const textColor = brightness > 128 ? '000000' : 'FFFFFF';
  
  // Format the text for URL (replace spaces with +)
  const formattedText = encodeURIComponent(text).replace(/%20/g, '+');
  
  // Return the placehold.co URL
  return `https://placehold.co/${width}x${height}/${bgColor}/${textColor}?text=${formattedText}`;
};

/**
 * Get image URL with fallback to placeholder if empty
 * @param src Original image URL
 * @param fallbackText Text to display on fallback image
 * @param width Optional width for the fallback image
 * @param height Optional height for the fallback image
 * @returns Image URL or fallback URL if original is empty
 */
export const getImageWithFallback = (
  src: string | undefined,
  fallbackText: string = 'No Image',
  width?: number,
  height?: number
): string => {
  if (!src || src === '') {
    // Sử dụng placeholder với text khi không có ảnh
    return getPlaceholderImage(fallbackText, width || 300, height || 200);
  }
  
  // Nếu là Cloudflare Image và có width hoặc height
  if (isCloudflareImageUrl(src) && (width || height)) {
    return resizeCloudflareImage(src, width, height);
  }
  
  return src;
};

/**
 * Tạo URL Cloudflare Images với thông số width và height
 * @param url URL ảnh Cloudflare Images
 * @param width Chiều rộng mong muốn
 * @param height Chiều cao mong muốn
 * @returns URL với thông số kích thước
 */
export const resizeCloudflareImage = (
  url: string,
  width?: number,
  height?: number
) => {
  if (!url || !url.includes('imagedelivery.net')) {
    return url;
  }

  // URL format: https://imagedelivery.net/account_hash/image_id/variant
  const parts = url.split('/');
  // Thay thế phần variant cuối cùng
  if (parts.length > 0) {
    let variant = parts.pop() || 'public';
    let params: string[] = [];
    
    if (width) params.push(`w=${width}`);
    if (height) params.push(`h=${height}`);
    
    if (params.length > 0) {
      variant = `${variant}?${params.join('&')}`;
    }
    
    parts.push(variant);
    return parts.join('/');
  }
  
  return url;
};

/**
 * Tạo URL thumbnail tối ưu cho hiển thị
 * @param url URL gốc
 * @param size Kích thước mong muốn (mặc định 300px)
 * @returns URL thumbnail đã tối ưu
 */
export const getThumbnailUrl = (url: string, size: number = 300) => {
  if (!url) return getImageWithFallback('');
  
  if (url.includes('imagedelivery.net')) {
    return resizeCloudflareImage(url, size, size);
  }
  
  return url;
};

/**
 * Tạo URL kích thước nhỏ cho hình ảnh từ các nhà cung cấp bên ngoài
 * @param url URL gốc của hình ảnh
 * @param width Chiều rộng mong muốn (mặc định 600px)
 * @param height Chiều cao mong muốn (không bắt buộc)
 * @returns URL đã tối ưu kích thước
 */
export const getOptimizedExternalImageUrl = (url: string, width: number = 600, height?: number): string => {
  if (!url) return url;
  
  try {
    // Xử lý URL từ Unsplash
    if (url.includes('unsplash.com') || url.includes('images.unsplash.com')) {
      // Unsplash hỗ trợ tham số w và q để điều chỉnh width và chất lượng
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?w=${width}&q=80`;
    }
    
    // Xử lý URL từ Pexels
    else if (url.includes('pexels.com') || url.includes('images.pexels.com')) {
      // Pexels API trả về các URL đã có kích thước (original, large, medium, small)
      // Thay đổi quy mô bằng cách thay thế phần cuối cùng của URL
      if (url.includes('/original')) {
        // Nếu là ảnh gốc, thay thế bằng cỡ nhỏ hơn
        return url.replace('/original', '/large');
      } else if (width <= 400) {
        // Nếu cần ảnh rất nhỏ
        return url.replace(/\/(large|medium)/, '/small');
      } else if (width <= 800) {
        // Nếu cần ảnh cỡ vừa
        return url.replace(/\/(original|large)/, '/medium');
      }
    }
    
    // Xử lý URL từ Pixabay
    else if (url.includes('pixabay.com')) {
      // Pixabay cung cấp nhiều URL với các kích thước khác nhau
      if (url.includes('largeImageURL') || url.includes('fullHD')) {
        return url.replace(/\/(largeImageURL|fullHD)/, '/webformatURL');
      }
    }
    
    // Xử lý URL từ Cloudflare Images
    else if (url.includes('imagedelivery.net')) {
      return resizeCloudflareImage(url, width, height);
    }
    
    // Trả về URL gốc nếu không phải URL từ những nguồn đã biết
    return url;
  } catch (error) {
    console.error('Error optimizing external image URL:', error);
    // Trả về URL gốc nếu có lỗi xảy ra
    return url;
  }
};

/**
 * Kiểm tra xem URL có phải là Cloudflare Images URL hay không
 * @param url URL cần kiểm tra
 * @returns true nếu là Cloudflare Images URL
 */
export const isCloudflareImageUrl = (url: string): boolean => {
  return !!url && url.includes('imagedelivery.net');
};

/**
 * Thay đổi biến thể của Cloudflare Images URL
 * @param url URL ảnh Cloudflare Images
 * @param variant Biến thể mới (public, thumbnail, etc.)
 * @returns URL với biến thể mới
 */
export const changeImageVariant = (url: string, variant: string = 'public'): string => {
  if (!url || !url.includes('imagedelivery.net')) {
    return url;
  }
  
  const urlObj = new URL(url);
  const pathParts = urlObj.pathname.split('/');
  
  // Thay thế thành phần cuối cùng của path với variant mới
  if (pathParts.length > 0) {
    pathParts[pathParts.length - 1] = variant;
    
    // Tái tạo URL với path mới
    urlObj.pathname = pathParts.join('/');
    return urlObj.toString();
  }
  
  return url;
}; 